import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { ProjectConnectionEditRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { projectConnectionMutations } from "@/queries/project-connections/mutations";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type FormValues, ProjectConnectionForm } from "./ProjectConnectionForm";

export function EditProjectConnectionPage(): React.ReactNode {
  const { id: connectionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { data: projects = [], isPending: isLoadingProjects, error: projectsError } = useLinkedProjects();
  const queries = useProjectConnectionQueries();
  const detailsQuery = useQuery(queries.details(connectionId!));
  const editConnection = projectConnectionMutations.useUpdateProjectConnection();

  const error = detailsQuery.error || projectsError;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (detailsQuery.isFetching || isLoadingProjects || !detailsQuery.data) {
    return <FullSizeLoader withPadding />;
  }
  const details = detailsQuery.data;

  const handleSubmit = async (values: FormValues) => {
    if (values.backgroundImage.length === 0) {
      showFlashToast({ variant: "alert", message: t("page.project-connections.actions.create.notification.error") });

      return;
    }
    const image = await uploadFormImage(values.backgroundImage[0]);
    if (!image) {
      return;
    }
    const payload: ProjectConnectionEditRequest = {
      name: values.name,
      description: values.description,
      content: values.content,
      contentTranslations: [],
      backgroundImageId: image.id,
    };
    await editConnection.mutateAsync({ connectionId: connectionId!, connection: payload });
    void navigate(routes.projectConnections.list());
  };

  return (
    <ProjectConnectionForm
      isEditMode
      defaultFormValues={{
        name: details.name,
        description: details.internalDescription,
        content: details.content,
        backgroundImage: details.backgroundImage ? [details.backgroundImage] : [],
        projects: details.projects,
      }}
      projects={projects.map((project) => ({ ...project, baseLanguageId: project.projectBaseLanguageId }))}
      otherConnections={[]}
      isSubmitting={isUploadingImage || editConnection.isPending}
      onSubmit={handleSubmit}
    />
  );
}
